<template>
    <div class="bg-gray-100 pt-[5.25rem] lg:pt-24">
        <div class="container">
            <div class="grid grid-cols-12 base-responsive-gap">
                {{ /* * */ }}
                {{ /* * */ }}
                {{ /* * */ }}
                {{ /* DETAILS */ }}
                {{ /* * */ }}
                <div class="col-span-12 lg:col-span-4 order-2 lg:order-1">
                    <h1 class="header-2 uppercase">{{ model.name }}</h1>
                    <h3 class="lg-lead mt-2 md:mt-4">{{ model.subtitle }}</h3>
                    <div class="md-lead border-t-[1px] border-gray-600 pt-4 mt-4 md:pt-8 md:mt-8">
                        {{ /* $getFormattedEventInterval has logic regarding the text_date_only prop as well. */ }}
                        {{ $getFormattedEventInterval(model).displayed }}

                        <div v-if="!model.text_date_only && model.date" class="mt-2">{{ model.date }}</div>
                    </div>

                    <div v-if="eventLocation" class="mt-4 md:mt-8">
                        <div class="font-Poppins font-semibold text-sm uppercase">
                            <T t="event.location" />
                        </div>
                        <div class="font-Poppins lg:text-lg font-semibold mt-1">{{ eventLocation }}</div>
                        <div v-if="model.place" class="font-Poppins lg:text-lg font-semibold mt-1">
                            {{ model.place }}
                        </div>
                    </div>

                    <div v-if="model.organizer" class="mt-4 md:mt-8">
                        <div class="font-Poppins font-semibold text-sm uppercase">
                            <T t="event.organizer" />
                        </div>
                        <div class="font-Poppins lg:text-lg font-semibold mt-1">
                            {{ model.organizer }}
                        </div>
                    </div>

                    <!-- Save in calendar -->
                    <div class="mt-4 md:mt-8">
                        <div class="flex items-center">
                            <i class="pi pi-calendar text-xl"></i>
                            <div class="uppercase ml-4"><T t="event.save_to_calendar" /></div>
                            <div class="flex ml-2">
                                <a
                                    :href="googleCalendarUrl"
                                    target="_blank"
                                    class="w-10 h-10 bg-contain bg-center bg-no-repeat ml-5"
                                    :style="`background-image: url(${require('~/assets/images/icons/google.png')})`"
                                ></a>
                            </div>
                        </div>
                    </div>

                    <!-- info -->
                    <div v-if="model.description" class="border-t-[1px] border-gray-600 pt-4 mt-4">
                        <div class="flex items-start">
                            <i class="pi pi-info-circle text-xl"></i>
                            <div class="ml-4" v-html="model.description"></div>
                        </div>
                    </div>

                    <!-- share -->
                    <div class="border-t-[1px] border-gray-600 pt-4 mt-4">
                        <div class="flex items-start">
                            <i class="pi pi-share-alt text-xl"></i>
                            <a
                                :href="`https://www.facebook.com/sharer/sharer.php?u=${
                                    $config.siteBaseURL + $route.fullPath
                                }`"
                                target="_blank"
                                class="uppercase ml-4"
                            >
                                <T t="event.share" />
                            </a>
                        </div>
                    </div>

                    <div class="-mx-2">
                        <Button
                            v-if="model.ticket_url"
                            :to="model.ticket_url"
                            target="_blank"
                            :label="$t('generic.purchase_ticket')"
                            class="mt-8 mx-2"
                        />

                        <Button
                            v-if="model.registration_url"
                            :to="model.registration_url"
                            target="_blank"
                            :label="$t('event.register')"
                            class="mt-8 mx-2"
                        />
                    </div>
                </div>

                {{ /* * */ }}
                {{ /* * */ }}
                {{ /* * */ }}
                {{ /* HERO IMAGE */ }}
                {{ /* * */ }}
                <div v-if="model.media.header_image" class="col-span-12 lg:col-span-8 order-1 lg:order-2 relative">
                    <div
                        class="aspect-w-16 aspect-h-9 lg:aspect-none 3xl:aspect-w-16 3xl:aspect-h-9 h-full w-full relative lg:absolute 3xl:relative"
                    >
                        <picture>
                            <source media="(min-width:992px)" :srcset="$getMediaImage(model.media, 'hero', 'lg')" />
                            <source media="(min-width:480px)" :srcset="$getMediaImage(model.media, 'hero', 'md')" />
                            <img
                                :src="$getMediaImage(model.media, 'hero', 'sm')"
                                :alt="model.name"
                                class="w-full h-full object-cover object-center base-responsive-rounding"
                            />
                        </picture>
                    </div>
                </div>
            </div>
        </div>

        {{ /* * */ }}
        {{ /* * */ }}
        {{ /* * */ }}
        {{ /* CONTENT */ }}
        {{ /* * */ }}
        <EditableContentComponentGroup :components="model.components" class="mt-12 sm:mt-20" />

        {{ /* * */ }}
        {{ /* * */ }}
        {{ /* * */ }}
        {{ /* PROGRAM RECOMMENDATIONS */ }}
        {{ /* * */ }}
        <section v-if="recommendedEvents.length" class="mt-16 sm:mt-20">
            <div class="container grid grid-cols-12 mb-10">
                <div class="col-span-12 sm:col-span-6 lg:col-span-7">
                    <h2 class="header-2"><T t="event.recommended_events" /></h2>
                </div>
            </div>
            <CardCarousel type="program" :items="recommendedEvents" />
        </section>
    </div>
</template>

<script>
import CardCarousel from '@/components/Carousels/CardCarousel/CardCarousel.vue';
import EditableContentComponentGroup from '@/components/Generic/EditableContentComponentGroup.vue';

export default {
    name: 'EventEndpageTemplate',
    components: {
        EditableContentComponentGroup,
        CardCarousel,
    },
    props: {
        model: {
            type: Object,
            required: true,
        },
        recommendedEvents: {
            type: Array,
            required: true,
        },
    },
    computed: {
        eventLocation() {
            return this.$getEventLocationName(this.model.location_id);
        },
        googleCalendarUrl() {
            return this.$generateGoogleCalendarUrl({
                title: this.model.name,
                details: this.model.lead_text,
                location: this.eventLocation,
                startDate: this.model.start_date,
                endDate: this.model.end_date,
            });
        },
    },
};
</script>

<style scoped></style>
